import ICAL from 'ical.js';
import {
  formatJuarezDate,
  formatJuarezTime,
  formatJuarezDay,
  formatJuarezMonthShort,
  formatJuarezWeekday,
  juarezWallClockToDate,
} from './timezone';

export const DEFAULT_ICS_URL =
  'https://calendar.google.com/calendar/ical/e3d32c8646dc3721882e86747b956eb034c8fcb8b59fa8201772c2a89008c52c%40group.calendar.google.com/public/basic.ics';

export const CALENDAR_FEED_PATH = '/api/calendar';

const IMAGE_TAG = /\[imagen:\s*(https?:\/\/[^\s\]]+)\]/gi;
const PEOPLE_KIND = 'organizador(?:as|es|a)?|invitad[oa]s?';
const PEOPLE_TAG = new RegExp(`\\[(${PEOPLE_KIND}):\\s*([^\\]]+)\\]`, 'gi');
const REGISTER_TAG = /\[(?:inscripci[oó]n|registro):\s*(https?:\/\/[^\s\]]+)\]/gi;
/** Permite <span> y <br> de Google Calendar dentro de los corchetes. */
const TAG_INSIDE = '(?:[^\\]<]|<[^>]*>)*?';
const PEOPLE_TAG_HTML = new RegExp(
  `\\[${TAG_INSIDE}(?:${PEOPLE_KIND})${TAG_INSIDE}:${TAG_INSIDE}\\]`,
  'gi',
);
const IMAGE_TAG_HTML = new RegExp(`\\[${TAG_INSIDE}imagen${TAG_INSIDE}:${TAG_INSIDE}\\]`, 'gi');
const REGISTER_TAG_HTML = new RegExp(
  `\\[${TAG_INSIDE}(?:inscripci[oó]n|registro)${TAG_INSIDE}:${TAG_INSIDE}\\]`,
  'gi',
);
const HTML_TAG = /<\/?[a-z][\s\S]*>/i;
const HORIZON_MONTHS = 6;
const MAX_OCCURRENCES = 240;
const ALLOWED_HTML = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'H1', 'H2', 'H3', 'SPAN', 'UL', 'OL', 'LI', 'BLOCKQUOTE']);
const WEEKDAYS: Record<string, string> = {
  SU: 'domingo',
  MO: 'lunes',
  TU: 'martes',
  WE: 'miércoles',
  TH: 'jueves',
  FR: 'viernes',
  SA: 'sábado',
};

export interface ChurchEvent {
  uid: string;
  start: Date;
  end: Date;
  allDay: boolean;
  title: string;
  location: string;
  description: string;
  descriptionHtml: string;
  excerpt: string;
  images: string[];
  organizers: string[];
  guests: string[];
  registrationUrl?: string;
  recurring: boolean;
  recurrenceLabel?: string;
  slug: string;
}

export function slugifyTitle(title: string) {
  const slug = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72);
  return slug || 'evento';
}

function slugDate(date: Date) {
  const day = formatJuarezDay(date);
  const month = formatJuarezMonthShort(date).toLowerCase().replace(/\./g, '');
  return `${day}-${month}`;
}

export function assignEventSlugs(events: ChurchEvent[]) {
  const used = new Set<string>();
  for (const event of events) {
    let base = slugifyTitle(event.title);
    if (base === 'detalle') base = `${base}-${slugDate(event.start)}`;
    let slug = base;
    if (used.has(slug)) slug = `${base}-${slugDate(event.start)}`;
    let extra = 2;
    while (used.has(slug)) {
      slug = `${base}-${slugDate(event.start)}-${extra}`;
      extra += 1;
    }
    used.add(slug);
    event.slug = slug;
  }
}

export function eventDetailHref(event: ChurchEvent) {
  return `/eventos/${event.slug}/`;
}

export function eventShareImage(event: ChurchEvent) {
  const cover = event.images[0];
  if (!cover) return undefined;
  return expandImageSources(cover)[0];
}

export function extractDriveId(raw: string) {
  try {
    const url = new URL(raw);
    const fromQuery = url.searchParams.get('id');
    if (fromQuery) return fromQuery;
    const fromPath = url.pathname.match(/\/(?:file\/)?d\/([a-zA-Z0-9_-]+)/);
    return fromPath?.[1] ?? null;
  } catch {
    return raw.match(/[?&]id=([a-zA-Z0-9_-]+)/)?.[1] ?? null;
  }
}

export function expandImageSources(raw: string) {
  const id = extractDriveId(raw);
  if (id) {
    return [
      `https://lh3.googleusercontent.com/d/${id}=w2000`,
      `https://drive.google.com/thumbnail?id=${id}&sz=w2000`,
      `https://drive.google.com/uc?export=view&id=${id}`,
    ];
  }
  try {
    const url = new URL(raw);
    if (url.protocol === 'http:' || url.protocol === 'https:') return [url.href];
  } catch {
    // ignore
  }
  return [];
}

function keepImageUrl(raw: string) {
  const sources = expandImageSources(raw);
  return sources.length ? raw : null;
}

function uniqueUrls(urls: string[]) {
  return [...new Set(urls.filter(Boolean))];
}

function decodeEntities(value: string) {
  const named = value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(parseInt(code, 16)));

  if (typeof document === 'undefined') return named;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = named;
  return textarea.value;
}

/** Texto plano para leer etiquetas, aunque Google las parta con HTML. */
function flattenMarkup(value: string) {
  return decodeEntities(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '');
}

export function htmlToPlainText(html: string) {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n\n')
    .replace(/<[^>]+>/g, ' ');
  return decodeEntities(withBreaks).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/[ \t]{2,}/g, ' ').trim();
}

export function sanitizeEventHtml(html: string) {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const clean = (node: Node) => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (!ALLOWED_HTML.has(el.tagName)) {
          const parent = el.parentNode;
          while (el.firstChild) parent?.insertBefore(el.firstChild, el);
          parent?.removeChild(el);
          continue;
        }
        [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
        clean(el);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
      }
    }
  };

  clean(doc.body);
  return doc.body.innerHTML.trim();
}

function splitNames(raw: string) {
  return raw
    .split(/[,;|/]+/)
    .map((name) => name.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function uniqueNames(names: string[]) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of names) {
    const key = name.toLocaleLowerCase('es-MX');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(name);
  }
  return result;
}

export function parseImagesAndDescription(raw: string | null | undefined) {
  for (const pattern of [IMAGE_TAG, REGISTER_TAG, PEOPLE_TAG, IMAGE_TAG_HTML, REGISTER_TAG_HTML, PEOPLE_TAG_HTML]) {
    pattern.lastIndex = 0;
  }

  const source = raw ?? '';
  const flat = flattenMarkup(source);
  const tagged: string[] = [];
  const organizers: string[] = [];
  const guests: string[] = [];
  let registrationUrl: string | undefined;

  flat.replace(IMAGE_TAG, (_, url: string) => {
    const kept = keepImageUrl(url.trim());
    if (kept) tagged.push(kept);
    return '';
  });

  flat.replace(REGISTER_TAG, (_, url: string) => {
    try {
      const parsed = new URL(url.trim());
      if (!registrationUrl && (parsed.protocol === 'http:' || parsed.protocol === 'https:')) {
        registrationUrl = parsed.href;
      }
    } catch {
      // enlace inválido
    }
    return '';
  });

  flat.replace(PEOPLE_TAG, (_, kind: string, names: string) => {
    const parsed = splitNames(flattenMarkup(names));
    if (/^organizador/i.test(kind)) organizers.push(...parsed);
    else guests.push(...parsed);
    return '';
  });

  let withoutTags = source
    .replace(IMAGE_TAG_HTML, '')
    .replace(REGISTER_TAG_HTML, '')
    .replace(PEOPLE_TAG_HTML, '');
  withoutTags = withoutTags.replace(/<p>\s*<\/p>/gi, '').trim();

  const isHtml = HTML_TAG.test(withoutTags);
  const descriptionHtml = isHtml ? sanitizeEventHtml(withoutTags) : '';
  const description = isHtml
    ? htmlToPlainText(withoutTags)
    : flattenMarkup(withoutTags).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();

  return {
    images: uniqueUrls(tagged),
    description,
    descriptionHtml,
    organizers: uniqueNames(organizers),
    guests: uniqueNames(guests),
    registrationUrl,
  };
}

function makeExcerpt(text: string, max = 160) {
  if (!text) return '';
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const cut = slice.lastIndexOf(' ');
  return `${(cut > 80 ? slice.slice(0, cut) : slice).trim()}…`;
}

function joinSpanish(items: string[]) {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} y ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

function formatRrule(vevent: InstanceType<typeof ICAL.Component>) {
  const rrule = vevent.getFirstPropertyValue('rrule') as InstanceType<typeof ICAL.Recur> | string | null;
  if (!rrule || typeof rrule === 'string') return undefined;

  const freq = String(rrule.freq ?? '').toUpperCase();
  const interval = Number(rrule.interval || 1);
  const byday = (rrule.getComponent('byday') as string[]).map((day) => WEEKDAYS[day.replace(/^-?\d+/, '')]).filter(Boolean);

  if (freq === 'WEEKLY') {
    if (interval === 1 && byday.length) return `Cada ${joinSpanish(byday)}`;
    if (interval === 1) return 'Cada semana';
    return `Cada ${interval} semanas`;
  }
  if (freq === 'DAILY') return interval === 1 ? 'Todos los días' : `Cada ${interval} días`;
  if (freq === 'MONTHLY') return interval === 1 ? 'Cada mes' : `Cada ${interval} meses`;
  if (freq === 'YEARLY') return interval === 1 ? 'Cada año' : `Cada ${interval} años`;
  return 'Recurrente';
}

function parseAttachImages(vevent: InstanceType<typeof ICAL.Component>) {
  const urls: string[] = [];
  for (const prop of vevent.getAllProperties('attach')) {
    const fmttype = String(prop.getParameter('fmttype') ?? '').toLowerCase();
    const filename = String(prop.getParameter('filename') ?? '').toLowerCase();
    const value = String(prop.getFirstValue() ?? '');
    const looksImage =
      fmttype.startsWith('image/') ||
      /\.(jpe?g|png|gif|webp|avif|svg)$/i.test(filename) ||
      /drive\.google\.com|googleusercontent\.com/i.test(value);
    if (!looksImage) continue;
    const kept = keepImageUrl(value);
    if (kept) urls.push(kept);
  }
  return uniqueUrls(urls);
}

function registerTimezones(comp: InstanceType<typeof ICAL.Component>) {
  for (const tzComp of comp.getAllSubcomponents('vtimezone')) {
    try {
      ICAL.TimezoneService.register(tzComp);
    } catch {
      // zona duplicada o inválida
    }
  }
}

function icalTimeToDate(time: InstanceType<typeof ICAL.Time>) {
  if (time.isDate) {
    return new Date(Date.UTC(time.year, time.month - 1, time.day, 12, 0, 0));
  }

  const tzid = time.zone?.tzid ?? 'floating';
  if (tzid !== 'floating') {
    return new Date(time.toUnixTime() * 1000);
  }

  return juarezWallClockToDate(
    time.year,
    time.month,
    time.day,
    time.hour,
    time.minute,
    time.second,
  );
}

export function parseIcs(icsText: string, now = new Date()): ChurchEvent[] {
  const jcal = ICAL.parse(icsText);
  const comp = new ICAL.Component(jcal);
  registerTimezones(comp);

  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + HORIZON_MONTHS);
  const nowMs = now.getTime();
  const events: ChurchEvent[] = [];

  const vevents = comp.getAllSubcomponents('vevent');
  const masterUids = new Set(
    vevents
      .filter((item) => !item.getFirstPropertyValue('recurrence-id'))
      .map((item) => String(item.getFirstPropertyValue('uid') ?? '')),
  );

  for (const vevent of vevents) {
    const status = String(vevent.getFirstPropertyValue('status') ?? '').toUpperCase();
    if (status === 'CANCELLED') continue;

    const event = new ICAL.Event(vevent);
    const title = (event.summary || 'Evento').trim();
    const hasDetails = Boolean(event.description || event.location || vevent.getFirstProperty('attach'));
    if (/^busy$/i.test(title) && !hasDetails) continue;

    const isOrphanException = event.isRecurrenceException() && !masterUids.has(event.uid);
    if (event.isRecurrenceException() && !isOrphanException) continue;

    const parsed = parseImagesAndDescription(event.description);
    const images = uniqueUrls([...parsed.images, ...parseAttachImages(vevent)]);
    const location = (event.location || '').trim();
    const excerpt = makeExcerpt(parsed.description);
    const recurring = !isOrphanException && event.isRecurring();
    const recurrenceLabel = recurring ? formatRrule(vevent) : undefined;

    const pushOccurrence = (
      startTime: InstanceType<typeof ICAL.Time>,
      endTime: InstanceType<typeof ICAL.Time>,
    ) => {
      const start = icalTimeToDate(startTime);
      const end = icalTimeToDate(endTime);
      if (end.getTime() < nowMs) return false;
      if (start.getTime() > horizon.getTime()) return false;

      events.push({
        uid: event.uid,
        start,
        end,
        allDay: Boolean(startTime.isDate),
        title,
        location,
        description: parsed.description,
        descriptionHtml: parsed.descriptionHtml,
        excerpt,
        images,
        organizers: parsed.organizers,
        guests: parsed.guests,
        registrationUrl: parsed.registrationUrl,
        recurring,
        recurrenceLabel,
        slug: '',
      });
      return true;
    };

    try {
      if (recurring) {
        const iterator = event.iterator();
        let count = 0;
        let next = iterator.next();
        while (next && count < MAX_OCCURRENCES) {
          const details = event.getOccurrenceDetails(next);
          if (pushOccurrence(details.startDate, details.endDate)) break;
          count += 1;
          next = iterator.next();
        }
      } else {
        pushOccurrence(event.startDate, event.endDate);
      }
    } catch {
      // evento malformado: se omite
    }
  }

  events.sort((a, b) => a.start.getTime() - b.start.getTime());
  assignEventSlugs(events);
  return events;
}

export function findEventBySlug(events: ChurchEvent[], slug: string) {
  return events.find((item) => item.slug === slug);
}

export function readEventSlug(pathname = typeof window === 'undefined' ? '' : window.location.pathname) {
  const path = pathname.replace(/\/+$/, '') || '/';
  const match = path.match(/^\/eventos\/([^/]+)$/);
  if (!match || match[1] === 'detalle') return null;
  return decodeURIComponent(match[1]);
}

export function findEvent(events: ChurchEvent[], uid: string, startIso?: string | null) {
  if (startIso) {
    const target = Date.parse(startIso);
    if (!Number.isNaN(target)) {
      const exact = events.find(
        (item) => item.uid === uid && Math.abs(item.start.getTime() - target) < 2000,
      );
      if (exact) return exact;
    }
  }
  return events.find((item) => item.uid === uid);
}

let calendarRequest: Promise<ChurchEvent[]> | null = null;

export async function fetchCalendarEvents(): Promise<ChurchEvent[]> {
  if (!calendarRequest) {
    const stamp = Math.floor(Date.now() / 60000);
    calendarRequest = fetch(`${CALENDAR_FEED_PATH}?v=${stamp}`, {
      headers: { Accept: 'text/calendar', 'Cache-Control': 'no-cache' },
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('No se pudo cargar el calendario');
        return parseIcs(await response.text());
      })
      .catch((error) => {
        calendarRequest = null;
        throw error;
      });
  }
  return calendarRequest;
}

export function formatEventTimeRange(event: ChurchEvent) {
  if (event.allDay) {
    const spanDays = Math.max(
      1,
      Math.round((event.end.getTime() - event.start.getTime()) / 86400000),
    );
    return spanDays > 1 ? `${spanDays} días` : 'Todo el día';
  }

  const start = formatJuarezTime(event.start);
  const end = formatJuarezTime(event.end);
  return start === end ? start : `${start} – ${end}`;
}

export function formatEventCardDate(event: ChurchEvent) {
  return {
    day: formatJuarezDay(event.start),
    month: formatJuarezMonthShort(event.start),
    weekday: formatJuarezWeekday(event.start),
    longDate: formatJuarezDate(event.start),
    time: formatEventTimeRange(event),
  };
}

export function personInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part.charAt(0).toLocaleUpperCase('es-MX')).join('');
}
